/* ==========================================================================
   Appointments — booking page controller
   --------------------------------------------------------------------------
   One script for /pages/book-an-appointment (and any /pages/book-<handle>
   page reusing the template). One palette, no per-service theming.

     • service picked from a <select> (?service= kept in the URL);
     • a week grid of time slots (Mon–Sun, morning / afternoon / evening,
       "Show more" per day, ‹ › weeks, Today / First available);
     • details + questions + consent on one form, then confirm;
     • summary sidebar (booking · location · when · about this appointment);
     • confirmation (?booked=<id>&t=), manage (?manage=<id>&t=) and party
       (?party=<id>&t=) views.

   Talks to the middleware through the App Proxy (/apps/book/*), or to the
   fixture (assets/booking-mock.json) while "Use fixture data" is on.
   Contract: docs/appointments-theme-handoff-17-08-2026.md — envelope
   {ok, data, error{code,message}}, ISO-8601 with offset, Europe/London.
   State kept in sessionStorage so a refresh doesn't lose the booking.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__mccallsBooking) return; // the flow section and theme.liquid may both load this file
  window.__mccallsBooking = true;

  var TZ = 'Europe/London';
  var HOLD_MINUTES = 10;
  var SLOTS_PER_DAY = 6;      // shown per column before "Show more"
  var FIRST_SLOT_CHUNK = 35;  // days per availability request when hunting the first free slot

  /* ---------- tiny utils ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function attr(s) { return esc(s); }
  function parseJSON(text, fallback) { try { return JSON.parse(text); } catch (e) { return fallback; } }
  function uid(prefix) { return (prefix || 'id') + '_' + Math.random().toString(36).slice(2, 10); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function pluralise(n, one, many) { return n === 1 ? one : (many || one + 's'); }
  function q(name) { return new URLSearchParams(location.search).get(name); }
  function setQuery(params, replace) {
    var sp = new URLSearchParams(location.search);
    Object.keys(params).forEach(function (k) {
      if (params[k] == null || params[k] === '') sp.delete(k); else sp.set(k, params[k]);
    });
    var url = location.pathname + (sp.toString() ? '?' + sp.toString() : '');
    try { history[replace ? 'replaceState' : 'pushState']({}, '', url); } catch (e) { /* noop */ }
  }
  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'booking_' + event }, data || {}));
  }

  /* ---------- dates (Europe/London) ---------- */
  function tzParts(date, tz) {
    var f = new Intl.DateTimeFormat('en-GB', { timeZone: tz || TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    var o = {};
    f.formatToParts(date).forEach(function (p) { if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10); });
    if (o.hour === 24) o.hour = 0;
    return o;
  }
  function tzOffsetMinutes(date, tz) {
    var p = tzParts(date, tz);
    var asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return Math.round((asUTC - date.getTime()) / 60000);
  }
  /* Build a Date for wall-clock y/m/d h:mi in London */
  function londonDate(y, m, d, h, mi) {
    var guess = new Date(Date.UTC(y, m - 1, d, h || 0, mi || 0));
    var off = tzOffsetMinutes(guess);
    var out = new Date(guess.getTime() - off * 60000);
    var off2 = tzOffsetMinutes(out);
    if (off2 !== off) out = new Date(guess.getTime() - off2 * 60000);
    return out;
  }
  function toISOWithOffset(date) {
    var off = tzOffsetMinutes(date);
    var p = tzParts(date);
    var sign = off >= 0 ? '+' : '-';
    var a = Math.abs(off);
    return p.year + '-' + pad(p.month) + '-' + pad(p.day) + 'T' + pad(p.hour) + ':' + pad(p.minute) + ':' + pad(p.second) + sign + pad(Math.floor(a / 60)) + ':' + pad(a % 60);
  }
  function ymd(date) { var p = tzParts(date); return p.year + '-' + pad(p.month) + '-' + pad(p.day); }
  function fmt(date, opts) {
    return new Intl.DateTimeFormat('en-GB', Object.assign({ timeZone: TZ }, opts)).format(date);
  }
  var fmtDayLong = function (d) { return fmt(d, { weekday: 'long', day: 'numeric', month: 'long' }); };
  var fmtDayFull = function (d) { return fmt(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); };
  var fmtDayShort = function (d) { return fmt(d, { weekday: 'short', day: 'numeric', month: 'short' }); };
  var fmtTime = function (d) { return fmt(d, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }); };
  var fmtMonth = function (d) { return fmt(d, { month: 'long', year: 'numeric' }); };
  var fmtDateTime = function (d) { return fmtDayLong(d) + ', ' + fmtTime(d); };
  function addDays(date, n) { return new Date(date.getTime() + n * 86400000); }
  function parseYMD(s) { var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? londonDate(+m[1], +m[2], +m[3], 12, 0) : null; }
  function londonDow(date) { var p = tzParts(date); return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay(); } // 0 = Sun
  function startOfWeek(date) { var dow = (londonDow(date) + 6) % 7; return parseYMD(ymd(addDays(date, -dow))); } // Monday, London midday
  function icsStamp(date) { return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''); }

  /* ---------- storage ---------- */
  var store = {
    get: function (k, fb) { try { var v = sessionStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch (e) { return fb; } },
    set: function (k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
    del: function (k) { try { sessionStorage.removeItem(k); } catch (e) { /* noop */ } }
  };

  /* ==========================================================================
     API adapters
     ========================================================================== */
  function ProxyApi(base) {
    this.base = base.replace(/\/$/, '');
  }
  ProxyApi.prototype.request = function (method, path, body) {
    var opts = { method: method, headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body !== undefined) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    return fetch(this.base + path, opts).then(function (r) {
      return r.json().catch(function () { return { ok: false, error: { code: 'bad_response', message: 'The booking service gave an unexpected reply.' } }; })
        .then(function (json) {
          if (!json || json.ok !== true) {
            var err = new Error((json && json.error && json.error.message) || 'Something went wrong.');
            err.code = (json && json.error && json.error.code) || ('http_' + r.status);
            throw err;
          }
          return json.data;
        });
    }, function () {
      var err = new Error('We couldn\'t reach the booking service. Please check your connection and try again.');
      err.code = 'network';
      throw err;
    });
  };
  ProxyApi.prototype.services = function () { return this.request('GET', '/services'); };
  ProxyApi.prototype.availability = function (p) {
    var sp = new URLSearchParams(p).toString();
    return this.request('GET', '/availability?' + sp);
  };
  ProxyApi.prototype.hold = function (p) { return this.request('POST', '/holds', p); };
  ProxyApi.prototype.releaseHold = function (id) { return this.request('DELETE', '/holds/' + encodeURIComponent(id)); };
  ProxyApi.prototype.book = function (p) { return this.request('POST', '/bookings', p); };
  ProxyApi.prototype.booking = function (id, t) { return this.request('GET', '/bookings/' + encodeURIComponent(id) + '?t=' + encodeURIComponent(t || '')); };
  ProxyApi.prototype.reschedule = function (id, t, p) { return this.request('POST', '/bookings/' + encodeURIComponent(id) + '?t=' + encodeURIComponent(t || ''), Object.assign({ action: 'reschedule' }, p)); };
  ProxyApi.prototype.cancel = function (id, t, p) { return this.request('POST', '/bookings/' + encodeURIComponent(id) + '?t=' + encodeURIComponent(t || ''), Object.assign({ action: 'cancel' }, p)); };
  ProxyApi.prototype.party = function (id, t) { return this.request('GET', '/parties/' + encodeURIComponent(id) + '?t=' + encodeURIComponent(t || '')); };
  ProxyApi.prototype.partyMember = function (id, t, p) { return this.request('POST', '/parties/' + encodeURIComponent(id) + '/members?t=' + encodeURIComponent(t || ''), p); };
  ProxyApi.prototype.me = function () { return this.request('GET', '/me'); };
  ProxyApi.prototype.schools = function (query) { return this.request('GET', '/schools?q=' + encodeURIComponent(query || '')); };

  /* Fixture adapter — same surface, everything generated locally.
     Bookings/holds persist in sessionStorage so ?booked= / ?manage= work after reload. */
  function MockApi(url, hubUrl) {
    this.url = url;
    this.hubUrl = hubUrl;
    this._data = null;
    this.dbKey = 'mccalls:booking:mockdb';
  }
  MockApi.prototype.load = function () {
    var self = this;
    if (this._data) return Promise.resolve(this._data);
    return fetch(this.url).then(function (r) { return r.json(); }).then(function (d) { self._data = d; return d; });
  };
  MockApi.prototype.db = function () { return store.get(this.dbKey, { bookings: {}, holds: {} }); };
  MockApi.prototype.saveDb = function (db) { store.set(this.dbKey, db); };
  MockApi.prototype.delay = function (v) { return new Promise(function (res) { setTimeout(function () { res(v); }, 250); }); };
  MockApi.prototype.services = function () {
    return this.load().then(function (d) {
      var col = {};
      (d.colleagues || []).forEach(function (c) { col[c.id] = c; });
      return d.services.filter(function (s) { return s.active !== false; }).map(function (s) {
        return Object.assign({}, s, { colleague: s.colleague_id ? col[s.colleague_id] || null : null, location: d.location || null });
      });
    });
  };
  MockApi.prototype.location = function () { return this.load().then(function (d) { return d.location; }); };
  MockApi.prototype.availability = function (p) {
    var self = this;
    return this.load().then(function (d) {
      var tpl = d.availability_templates[p.service] || d.availability_templates.default;
      var svc = d.services.filter(function (s) { return s.handle === p.service; })[0] || {};
      var lead = (svc.lead_time_hours || 0) * 3600000;
      var horizon = addDays(new Date(), svc.horizon_days || 90);
      var from = parseYMD(p.from) || new Date();
      var to = parseYMD(p.to) || addDays(from, 35);
      var now = new Date();
      var db = self.db();
      var days = [];
      for (var t = from.getTime(); t <= to.getTime(); t += 86400000) {
        var day = new Date(t);
        var parts = tzParts(day);
        var dow = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
        var slots = [];
        (tpl.windows || []).forEach(function (w) {
          if (w.weekdays.indexOf(dow) === -1) return;
          var sh = +w.start.split(':')[0], sm = +w.start.split(':')[1];
          var eh = +w.end.split(':')[0], em = +w.end.split(':')[1];
          var startMin = sh * 60 + sm, endMin = eh * 60 + em;
          for (var m = startMin; m + (svc.duration_minutes || tpl.slot_minutes) <= endMin; m += tpl.slot_minutes) {
            var start = londonDate(parts.year, parts.month, parts.day, Math.floor(m / 60), m % 60);
            if (start.getTime() < now.getTime() + lead) continue;
            if (start.getTime() > horizon.getTime()) continue;
            var end = new Date(start.getTime() + (svc.duration_minutes || tpl.slot_minutes) * 60000);
            var id = p.service + '|' + toISOWithOffset(start);
            var cap = w.quiet ? 1 : (tpl.capacity || 1);
            // deterministic "already booked" texture so the calendar looks lived-in
            var h = 0; for (var i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
            if (!w.quiet && h % 6 === 0) cap = 0;
            else if (!w.quiet && h % 6 === 1) cap = Math.min(cap, 1);
            Object.keys(db.bookings).forEach(function (bid) {
              var b = db.bookings[bid];
              if (b.slot_id === id && b.status !== 'cancelled') cap -= 1;
            });
            var partySize = parseInt(p.party || '1', 10) || 1;
            if (cap <= 0) continue;
            slots.push({ id: id, start: toISOWithOffset(start), end: toISOWithOffset(end), capacity_left: cap, quiet: !!w.quiet, resource: svc.colleague_id || null, party_ok: partySize <= (svc.max_party || 1) });
          }
        });
        if (slots.length) days.push({ date: ymd(day), capacity: slots.reduce(function (a, s) { return a + s.capacity_left; }, 0), slots: slots });
      }
      return self.delay({ days: days, timezone: TZ });
    });
  };
  MockApi.prototype.hold = function (p) {
    var db = this.db();
    var id = uid('hold');
    var exp = new Date(Date.now() + HOLD_MINUTES * 60000);
    db.holds[id] = { id: id, slot_id: p.slot_id, service: p.service, expires_at: toISOWithOffset(exp) };
    this.saveDb(db);
    return this.delay({ hold_id: id, expires_at: toISOWithOffset(exp) });
  };
  MockApi.prototype.releaseHold = function (id) { var db = this.db(); delete db.holds[id]; this.saveDb(db); return this.delay({}); };
  MockApi.prototype._decorate = function (b) {
    var d = this._data;
    var svc = d.services.filter(function (s) { return s.handle === b.service_handle; })[0] || {};
    var col = (d.colleagues || []).filter(function (c) { return c.id === svc.colleague_id; })[0] || null;
    return {
      id: b.id, status: b.status, start: b.start, end: b.end,
      service: { handle: svc.handle, title: svc.title, short_title: svc.short_title, quiet_time: !!svc.quiet_time },
      colleague: col, location: d.location, customer: b.customer, party: b.party || [], answers: b.answers || {},
      manage_token: b.token,
      manage_url: this.hubUrl + '?service=' + encodeURIComponent(svc.handle) + '&manage=' + encodeURIComponent(b.id) + '&t=' + b.token,
      party_id: b.party_id || null,
      party_url: b.party_id ? this.hubUrl + '?service=' + encodeURIComponent(svc.handle) + '&party=' + encodeURIComponent(b.party_id) + '&t=' + b.token : null,
      ics_url: null
    };
  };
  MockApi.prototype.book = function (p) {
    var db = this.db();
    var hold = db.holds[p.hold_id];
    if (!hold) { var e = new Error('That time is no longer held. Please choose a time again.'); e.code = 'slot_taken'; return Promise.reject(e); }
    var id = 'MC' + Math.random().toString(36).slice(2, 8).toUpperCase();
    var svc = this._data.services.filter(function (s) { return s.handle === p.service; })[0] || {};
    var start = hold.slot_id.split('|')[1];
    var end = toISOWithOffset(new Date(new Date(start).getTime() + (svc.duration_minutes || 30) * 60000));
    var partyId = (p.party && p.party.length > 1 && (svc.max_party || 1) > 1) ? uid('party') : null;
    db.bookings[id] = { id: id, service_handle: p.service, slot_id: hold.slot_id, start: start, end: end, status: 'confirmed', customer: p.customer, party: p.party || [], answers: p.answers || {}, consent: p.consent || {}, token: 'mock', party_id: partyId, created_at: toISOWithOffset(new Date()) };
    delete db.holds[p.hold_id];
    this.saveDb(db);
    return this.delay(this._decorate(db.bookings[id]));
  };
  MockApi.prototype.booking = function (id) {
    var self = this;
    return this.load().then(function () {
      var b = self.db().bookings[id];
      if (!b) { var e = new Error('We couldn\'t find that appointment. The link may have expired.'); e.code = 'not_found'; throw e; }
      return self.delay(self._decorate(b));
    });
  };
  MockApi.prototype.reschedule = function (id, t, p) {
    var self = this;
    return this.load().then(function () {
      var db = self.db(); var b = db.bookings[id]; var hold = db.holds[p.hold_id];
      if (!b || !hold) { var e = new Error('That time is no longer available. Please choose again.'); e.code = 'slot_taken'; throw e; }
      var svc = self._data.services.filter(function (s) { return s.handle === b.service_handle; })[0] || {};
      b.slot_id = hold.slot_id; b.start = hold.slot_id.split('|')[1];
      b.end = toISOWithOffset(new Date(new Date(b.start).getTime() + (svc.duration_minutes || 30) * 60000));
      b.status = 'rescheduled'; delete db.holds[p.hold_id]; self.saveDb(db);
      return self.delay(self._decorate(b));
    });
  };
  MockApi.prototype.cancel = function (id, t, p) {
    var self = this;
    return this.load().then(function () {
      var db = self.db(); var b = db.bookings[id];
      if (!b) { var e = new Error('We couldn\'t find that appointment.'); e.code = 'not_found'; throw e; }
      b.status = 'cancelled'; b.cancel_reason = p && p.reason; self.saveDb(db);
      return self.delay(self._decorate(b));
    });
  };
  MockApi.prototype.party = function (id) {
    var self = this;
    return this.load().then(function () {
      var db = self.db();
      var owner = null;
      Object.keys(db.bookings).forEach(function (k) { if (db.bookings[k].party_id === id) owner = db.bookings[k]; });
      if (!owner) { var e = new Error('We couldn\'t find that party link.'); e.code = 'not_found'; throw e; }
      var svc = self._data.services.filter(function (s) { return s.handle === owner.service_handle; })[0] || {};
      var members = (db.partyMembers && db.partyMembers[id]) || [];
      return self.delay({
        id: id, service: { handle: svc.handle, title: svc.title }, host: owner.customer, start: owner.start,
        members: (owner.party || []).map(function (m) { var done = members.filter(function (x) { return x.name === m.name; })[0]; return { name: m.name, sizes_complete: !!done }; }),
        invite_url: self.hubUrl + '?service=' + encodeURIComponent(svc.handle) + '&party=' + encodeURIComponent(id) + '&t=mock',
        book_url: self.hubUrl + '?service=' + encodeURIComponent(svc.handle) + '&party=' + encodeURIComponent(id)
      });
    });
  };
  MockApi.prototype.partyMember = function (id, t, p) {
    var db = this.db(); db.partyMembers = db.partyMembers || {}; db.partyMembers[id] = (db.partyMembers[id] || []).concat([p]); this.saveDb(db);
    return this.delay({ ok: true });
  };
  MockApi.prototype.me = function () { return this.load().then(function (d) { return d.me; }); };
  MockApi.prototype.schools = function (query) {
    return this.load().then(function (d) {
      var qq = (query || '').toLowerCase();
      return (d.schools || []).filter(function (s) { return !qq || (s.name + ' ' + (s.keywords || '')).toLowerCase().indexOf(qq) !== -1; }).slice(0, 8);
    });
  };

  /* ==========================================================================
     Page controller
     ========================================================================== */
  var page = {
    root: null,          // [data-booking-flow]
    api: null,
    services: [],
    service: null,
    fixedHandle: '',     // /pages/book-<handle> — no dropdown
    hubUrl: '/pages/book-an-appointment',
    prefill: {},
    signedIn: false,
    reduceMotion: false,
    live: null,          // aria-live region
    state: null,
    stateKey: null,
    week: { start: null, days: {}, fetched: {}, loading: false, expanded: {} },
    location: null,
    policy: '',
    manage: null,
    party: null,
    icons: {
      arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M19 12H5m6 6-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      chevL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="m15 5-7 7 7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      chevR: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="m9 5 7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      tick: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      cal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="1.5"/><path d="M3.5 9.5h17M8 3v4M16 3v4" stroke-linecap="round"/></svg>',
      pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 21s6-5.2 6-10.5A6 6 0 0 0 6 10.5C6 15.8 12 21 12 21Z"/><circle cx="12" cy="10.5" r="2.25"/></svg>'
    }
  };

  function serviceByHandle(h) { return page.services.filter(function (s) { return s.handle === h; })[0] || null; }
  function sortedServices() { return page.services.slice().sort(function (a, b) { return (a.sort_order || 0) - (b.sort_order || 0); }); }

  /* ---------- view switching ---------- */
  function showServiceView(handle) {
    var svc = handle ? serviceByHandle(handle) : null;
    page.service = svc || null;
    $$('[data-booking-service-only]').forEach(function (el) { el.hidden = !svc; });
    $$('[data-service-panel]').forEach(function (el) { el.hidden = !svc || el.getAttribute('data-service-panel') !== svc.handle; });
    $$('[data-booking-render]').forEach(function (el) { renderInto(el, svc); });
    if (!page.baseTitle) page.baseTitle = document.title;
    document.title = (svc ? svc.title + ' — ' : '') + page.baseTitle;
  }

  /* JS-rendered FAQ for the fixture path (Liquid renders the same markup
     from metaobjects — keep the two in step). */
  function renderInto(el, svc) {
    var kind = el.getAttribute('data-booking-render');
    if (!svc) { el.innerHTML = ''; return; }
    if (kind === 'faq') el.innerHTML = renderFaq(svc);
  }
  function renderFaq(svc) {
    if (!svc.faqs || !svc.faqs.length) return '';
    return '<span class="bk-eyebrow">Good to know</span><h2 class="bk-title bk-title--sm">Questions</h2><div class="bk-faq__list">' +
      svc.faqs.map(function (f) { return '<details class="bk-faq__item"><summary class="bk-faq__q">' + esc(f.question) + '</summary><div class="bk-faq__a">' + esc(f.answer) + '</div></details>'; }).join('') + '</div>';
  }

  /* ==========================================================================
     Flow state
     ========================================================================== */
  function defaultState() {
    return { step: 0, party: { size: 1, children: [], members: [], companion: '' }, slot: null, hold: null, details: {}, answers: {}, consent: {}, event_date: '' };
  }
  function loadState() {
    page.stateKey = 'mccalls:booking:' + (page.service ? page.service.handle : 'none');
    page.state = store.get(page.stateKey, null) || defaultState();
    var pf = page.prefill || {};
    if (!page.state._prefilled) {
      if (pf.customer) page.state.details = Object.assign({ first_name: pf.customer.first_name || '', last_name: pf.customer.last_name || '', email: pf.customer.email || '', phone: pf.customer.phone || '' }, page.state.details);
      page.state._prefilled = true;
    }
    if (q('school') && !page.state.answers.school) page.state.answers.school = q('school');
    // A slot without a live hold on reload: keep the pick, the hold is retaken on confirm
  }
  function saveState() { store.set(page.stateKey, page.state); }
  function resetState() { store.del(page.stateKey); page.state = defaultState(); page.state._prefilled = true; }

  function fieldsOf(svc) { return (svc && svc.questions && svc.questions.fields) || []; }
  function whoFields(svc) { return fieldsOf(svc).filter(function (f) { return f.type === 'children' || f.type === 'school' || f.key === 'event_date' || f.key === 'wedding_date' || f.key === 'companion'; }); }
  function questionFields(svc) { var who = whoFields(svc); return fieldsOf(svc).filter(function (f) { return who.indexOf(f) === -1; }); }
  function partySize() {
    var st = page.state; if (!st) return 1;
    if (st.party.children && st.party.children.length) return st.party.children.length + 1;
    return st.party.size || 1;
  }

  var STEPS = [{ id: 'time', title: 'Choose a time' }, { id: 'details', title: 'Your details' }, { id: 'confirm', title: 'Confirm' }];
  function steps() { return page.service ? STEPS : []; }
  function currentStep() { var s = steps(); if (!s.length) return null; page.state.step = Math.min(page.state.step, s.length - 1); return s[page.state.step]; }

  /* ==========================================================================
     Shell: service bar + stepper + step mount + summary sidebar
     ========================================================================== */
  function renderShell() {
    var root = page.root; if (!root) return;
    var html = '<div class="bk-flow__main">';
    html += renderServiceBar();
    html += '<ol class="bk-stepper" data-stepper aria-label="Booking steps"></ol>';
    html += '<p class="bk-sr" data-flow-progress aria-live="polite"></p>';
    html += '<div class="bk-error" role="alert" data-flow-error></div>';
    html += '<div data-step-mount></div>';
    html += '</div>';
    html += '<aside class="bk-flow__side" data-summary aria-label="Summary"></aside>';
    root.innerHTML = html;
  }

  function renderServiceBar() {
    var svc = page.service;
    var html = '<div class="bk-service">';
    if (page.fixedHandle && svc) {
      html += '<span class="bk-service__label">Appointment</span><h2 class="bk-service__fixed">' + esc(svc.title) + '</h2>';
    } else {
      html += '<label class="bk-service__label" for="bk-service-select">Appointment</label>';
      html += '<div class="bk-service__control"><select class="bk-select bk-service__select" id="bk-service-select" data-service-select>';
      html += '<option value=""' + (svc ? '' : ' selected') + '>Choose an appointment type…</option>';
      sortedServices().forEach(function (s) {
        var label = s.short_title || s.title;
        if (s.duration_minutes) label += ' — ' + s.duration_minutes + ' min';
        html += '<option value="' + attr(s.handle) + '"' + (svc && svc.handle === s.handle ? ' selected' : '') + '>' + esc(label) + '</option>';
      });
      html += '</select></div>';
    }
    if (svc && svc.tagline) html += '<p class="bk-service__tagline">' + esc(svc.tagline) + '</p>';
    return html + '</div>';
  }

  function renderStepper() {
    var el = $('[data-stepper]', page.root); if (!el) return;
    var s = steps();
    if (!s.length) { el.innerHTML = ''; el.hidden = true; return; }
    el.hidden = false;
    var cur = page.state.step;
    el.innerHTML = s.map(function (step, i) {
      var cls = 'bk-stepper__item' + (i < cur ? ' is-done' : '') + (i === cur ? ' is-current' : '');
      var mark = i < cur ? page.icons.tick : String(i + 1);
      var canJump = i < cur;
      var inner = '<span class="bk-stepper__mark" aria-hidden="true">' + mark + '</span><span class="bk-stepper__label">' + esc(step.title) + '</span>';
      return '<li class="' + cls + '"' + (i === cur ? ' aria-current="step"' : '') + '>' + (canJump ? '<button type="button" class="bk-stepper__btn" data-action="goto" data-goto="' + attr(step.id) + '">' + inner + '</button>' : '<span class="bk-stepper__btn">' + inner + '</span>') + '</li>';
    }).join('');
    var prog = $('[data-flow-progress]', page.root);
    if (prog) prog.textContent = 'Step ' + (cur + 1) + ' of ' + s.length + ': ' + s[cur].title;
  }

  function renderSummary() {
    var el = $('[data-summary]', page.root); if (!el) return;
    var svc = page.service, st = page.state;
    var html = '<div class="bk-summary-card"><h2 class="bk-summary-card__title">Summary</h2>';
    if (!svc) {
      html += '<p class="bk-summary-card__empty">Choose an appointment type to see available times.</p>';
    } else {
      var loc = svc.location || page.location || {};
      var slot = st && st.slot;
      html += '<dl class="bk-summary-card__list">';
      html += '<div><dt>Booking</dt><dd>' + esc(svc.title) + (svc.duration_minutes ? '<small>' + esc(svc.duration_minutes) + ' minutes' + (svc.price_note ? ' · ' + esc(svc.price_note) : '') + '</small>' : '') + '</dd></div>';
      if (loc.name) html += '<div><dt>' + page.icons.pin + 'Location</dt><dd>' + esc(loc.name) + (loc.address ? '<small>' + esc(loc.address) + '</small>' : '') + '</dd></div>';
      html += '<div><dt>' + page.icons.cal + 'When</dt><dd' + (slot ? '' : ' class="is-empty"') + '>' + (slot ? esc(fmtDayLong(new Date(slot.start))) + '<small>' + esc(fmtTime(new Date(slot.start))) + (slot.end ? ' – ' + esc(fmtTime(new Date(slot.end))) : '') + '</small>' : 'Select a day and time') + '</dd></div>';
      html += '</dl>';
      html += renderAbout(svc);
      if (page.policy) html += '<p class="bk-summary-card__policy">' + esc(page.policy) + '</p>';
    }
    html += '</div>';
    el.innerHTML = html;
  }

  /* "About this appointment" — description, what to expect, what to bring, who you'll meet */
  function renderAbout(svc) {
    var parts = [];
    if (svc.description_html) parts.push('<div class="bk-rte">' + svc.description_html + '</div>');
    var steps = (svc.what_to_expect || []).map(function (s) {
      if (typeof s === 'string') { var p = s.split(' — '); return '<li>' + (p.length > 1 ? '<strong>' + esc(p[0]) + '</strong> ' + esc(p.slice(1).join(' — ')) : esc(s)) + '</li>'; }
      return '<li><strong>' + esc(s.title) + '</strong> ' + esc(s.body || '') + '</li>';
    });
    if (steps.length) parts.push('<h3>What to expect</h3><ol class="bk-about__list">' + steps.join('') + '</ol>');
    else if (svc.what_to_expect_html) parts.push('<h3>What to expect</h3><div class="bk-rte">' + svc.what_to_expect_html + '</div>');
    if (svc.what_to_bring && svc.what_to_bring.length) parts.push('<h3>What to bring</h3><ul class="bk-about__list">' + svc.what_to_bring.map(function (b) { return '<li>' + esc(b) + '</li>'; }).join('') + '</ul>');
    if (svc.colleague && svc.colleague.name) parts.push('<h3>Who you\'ll meet</h3><p>' + esc(svc.colleague.name) + (svc.colleague.role ? ', ' + esc(svc.colleague.role) : '') + '</p>');
    if (svc.max_party > 1) parts.push('<p class="bk-muted">Up to ' + esc(svc.max_party) + ' people.</p>');
    if (!parts.length) return '';
    return '<details class="bk-about"' + (page.aboutOpen ? ' open' : '') + ' data-about><summary>About this appointment</summary><div class="bk-about__body">' + parts.join('') + '</div></details>';
  }

  /* ---------- render current step ---------- */
  function renderFlow(opts) {
    opts = opts || {};
    var root = page.root, mount = $('[data-step-mount]', root);
    if (!root || !mount) return;
    var err = $('[data-flow-error]', root); if (err) err.textContent = opts.error ? opts.error : '';
    renderStepper();
    renderSummary();
    var cur = currentStep();
    var html = '';
    if (!cur) {
      html = '<section class="bk-step" data-step="none"><div class="bk-week bk-week--placeholder" aria-hidden="true">' + renderWeekPlaceholder() + '</div></section>';
    } else {
      html = '<section class="bk-step" data-step="' + attr(cur.id) + '">' +
        '<h2 class="bk-step__title" tabindex="-1" data-step-heading>' + esc(cur.title) + '</h2>' +
        renderStep(cur.id) + '</section>';
    }
    mount.innerHTML = html;
    if (cur) afterRender(cur.id);
    if (opts.focus !== false && cur) {
      var h = $('[data-step-heading]', root);
      if (h) h.focus({ preventScroll: true });
    }
    if (opts.scroll) root.scrollIntoView({ behavior: page.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function renderStep(id) {
    switch (id) {
      case 'time': return renderTimeStep();
      case 'details': return renderDetailsStep();
      case 'confirm': return renderConfirmStep();
    }
    return '';
  }
  function afterRender(id) {
    if (id === 'time') mountWeek();
    if (id === 'details') mountSchool();
  }

  function actions(opts) {
    opts = opts || {};
    var html = '<div class="bk-actions">';
    html += (opts.noBack ? '<span></span>' : '<button type="button" class="bk-btn bk-btn--quiet" data-action="back">' + page.icons.back + '<span>Back</span></button>');
    html += '<button type="' + (opts.submit ? 'submit' : 'button') + '" class="bk-btn"' + (opts.submit ? '' : ' data-action="' + (opts.action || 'next') + '"') + (opts.disabled ? ' disabled' : '') + '>' + esc(opts.label || 'Continue') + (opts.icon || page.icons.arrow) + '</button>';
    return html + '</div>';
  }

  /* ==========================================================================
     Step 1 — week grid
     ========================================================================== */
  function renderTimeStep() {
    return '<div class="bk-week" data-week></div>' + (page.state.hold ? renderHoldNote() : '');
  }
  function renderHoldNote() {
    var st = page.state;
    if (!st.hold || !st.hold.expires_at) return '';
    return '<p class="bk-hold" data-hold-note>This time is held for you until ' + esc(fmtTime(new Date(st.hold.expires_at))) + '.</p>';
  }
  function renderWeekPlaceholder() {
    var start = alignStart(new Date()), n = visibleDays();
    var html = '<div class="bk-week__head"><div class="bk-week__title"><span class="bk-week__nav"></span><span class="bk-week__month">' + esc(fmtMonth(start)) + '</span><span class="bk-week__nav"></span></div></div><div class="bk-week__grid" style="--bk-days:' + n + '">';
    for (var i = 0; i < n; i++) {
      var d = addDays(start, i);
      html += '<div class="bk-week__col"><div class="bk-week__day"><span class="bk-week__dow">' + esc(fmt(d, { weekday: 'short' })) + '</span><span class="bk-week__num">' + esc(fmt(d, { day: 'numeric' })) + '</span></div><div class="bk-week__slots"><span class="bk-week__none">—</span></div></div>';
    }
    return html + '</div><p class="bk-week__hint">Choose an appointment type above to see times.</p>';
  }

  /* 7 days per page on desktop (Mon–Sun), 3 on phones */
  var narrowMq = window.matchMedia ? window.matchMedia('(max-width: 760px)') : null;
  function visibleDays() { return narrowMq && narrowMq.matches ? 3 : 7; }
  function alignStart(date) {
    var today = parseYMD(ymd(new Date()));
    var d = visibleDays() === 7 ? startOfWeek(date) : parseYMD(ymd(date));
    if (visibleDays() !== 7 && d.getTime() < today.getTime()) d = today;
    return d;
  }
  function mountWeek() {
    var st = page.state;
    if (!page.week.start) {
      var base = st && st.slot ? new Date(st.slot.start) : new Date();
      page.week.start = alignStart(base);
    } else {
      page.week.start = alignStart(page.week.start);
    }
    drawWeek();
    loadWeek();
  }
  if (narrowMq && narrowMq.addEventListener) narrowMq.addEventListener('change', function () { if (page.week.start && $('[data-week]', page.root)) mountWeek(); });
  function horizonDate() { return addDays(new Date(), (page.service && page.service.horizon_days) || 90); }
  function visibleKeys() { var out = []; for (var i = 0; i < visibleDays(); i++) out.push(ymd(addDays(page.week.start, i))); return out; }
  function rangeFetched() { return visibleKeys().every(function (k) { return page.week.fetched[k]; }); }
  function markFetched(from, to) { for (var t = from.getTime(); t <= to.getTime(); t += 86400000) page.week.fetched[ymd(new Date(t))] = true; }
  function slotGroup(date) { var h = tzParts(date).hour; return h < 12 ? 'Morning' : (h < 17 ? 'Afternoon' : 'Evening'); }

  function drawWeek() {
    var box = $('[data-week]', page.root); if (!box) return;
    var start = page.week.start, todayKey = ymd(new Date());
    var n = visibleDays();
    var end = addDays(start, n - 1);
    var first = alignStart(new Date());
    var prevDisabled = start.getTime() <= first.getTime();
    var nextDisabled = addDays(start, n).getTime() > horizonDate().getTime();
    var sm = tzParts(start), em = tzParts(end);
    var monthLabel = sm.month === em.month ? fmtMonth(start) : fmt(start, { month: 'short' }) + ' – ' + fmt(end, { month: 'short', year: 'numeric' });
    var fetched = rangeFetched();
    var loading = page.week.loading && !fetched;
    var st = page.state || {};

    var html = '<div class="bk-week__head">';
    html += '<div class="bk-week__title"><button type="button" class="bk-week__nav" data-action="week-prev" aria-label="' + (n === 7 ? 'Previous week' : 'Previous days') + '"' + (prevDisabled ? ' disabled' : '') + '>' + page.icons.chevL + '</button>';
    html += '<h3 class="bk-week__month" aria-live="polite">' + esc(monthLabel) + '</h3>';
    html += '<button type="button" class="bk-week__nav" data-action="week-next" aria-label="' + (n === 7 ? 'Next week' : 'Next days') + '"' + (nextDisabled ? ' disabled' : '') + '>' + page.icons.chevR + '</button></div>';
    html += '<div class="bk-week__tools"><button type="button" class="bk-week__tool" data-action="week-today"' + (prevDisabled ? ' disabled' : '') + '>Today</button><button type="button" class="bk-week__tool" data-action="week-first">First available</button></div>';
    html += '</div>';
    html += '<div class="bk-week__grid" style="--bk-days:' + n + '" role="group" aria-label="Available times from ' + attr(fmtDayLong(start)) + '">';
    for (var i = 0; i < n; i++) {
      var d = addDays(start, i);
      var key = ymd(d);
      var info = page.week.days[key];
      var slots = (info && info.slots) || [];
      var isToday = key === todayKey;
      html += '<div class="bk-week__col' + (isToday ? ' is-today' : '') + (slots.length ? '' : ' is-empty') + '">';
      html += '<div class="bk-week__day"><span class="bk-week__dow">' + esc(fmt(d, { weekday: 'short' })) + '</span><span class="bk-week__num">' + esc(fmt(d, { day: 'numeric' })) + '</span>' + (isToday ? '<span class="bk-week__today">Today</span>' : '') + '</div>';
      html += '<div class="bk-week__slots">';
      if (loading) {
        html += '<span class="bk-week__skel"></span><span class="bk-week__skel"></span><span class="bk-week__skel"></span>';
      } else if (!slots.length) {
        html += '<span class="bk-week__none">No available slots</span>';
      } else {
        var expanded = !!page.week.expanded[key];
        var shown = expanded ? slots : slots.slice(0, SLOTS_PER_DAY);
        var lastGroup = null;
        shown.forEach(function (s) {
          var sd = new Date(s.start), ed = s.end ? new Date(s.end) : null;
          var g = slotGroup(sd);
          if (g !== lastGroup) { html += '<span class="bk-week__group">' + g + '</span>'; lastGroup = g; }
          var sel = st.slot && st.slot.id === s.id;
          var label = fmtDayLong(sd) + ', ' + fmtTime(sd) + (ed ? ' to ' + fmtTime(ed) : '') + (s.capacity_left != null && s.capacity_left <= 2 ? ', ' + s.capacity_left + ' left' : '');
          html += '<button type="button" class="bk-slot" data-action="slot" data-slot-id="' + attr(s.id) + '" data-day="' + key + '" aria-pressed="' + (sel ? 'true' : 'false') + '" aria-label="' + attr(label) + '">' +
            '<span class="bk-slot__time">' + esc(fmtTime(sd)) + '</span>' + (ed ? '<span class="bk-slot__sep" aria-hidden="true">–</span><span class="bk-slot__time">' + esc(fmtTime(ed)) + '</span>' : '') +
            (s.quiet ? '<span class="bk-slot__tag">Quiet</span>' : '') + '</button>';
        });
        if (slots.length > SLOTS_PER_DAY) html += '<button type="button" class="bk-week__more" data-action="week-more" data-day="' + key + '" aria-expanded="' + (expanded ? 'true' : 'false') + '">' + (expanded ? 'Show fewer' : 'Show more') + '</button>';
      }
      html += '</div></div>';
    }
    html += '</div>';
    if (!loading && fetched) {
      var any = visibleKeys().some(function (k2) { return page.week.days[k2] && page.week.days[k2].slots && page.week.days[k2].slots.length; });
      if (!any) html += '<p class="bk-week__hint">Nothing free here — try the next ' + (n === 7 ? 'week' : 'days') + ' or <button type="button" class="bk-link bk-link--btn" data-action="week-first">jump to the first available time</button>.</p>';
    }
    box.innerHTML = html;
  }

  function loadWeek() {
    var start = page.week.start;
    if (rangeFetched()) { drawWeek(); return Promise.resolve(); }
    page.week.loading = true;
    drawWeek();
    var endD = addDays(start, visibleDays() - 1);
    return page.api.availability({ service: page.service.handle, from: ymd(start), to: ymd(endD), party: partySize() }).then(function (data) {
      cacheDays(data);
      markFetched(start, endD);
      page.week.loading = false;
      // First visit with nothing this week: hop forward once
      var any = (data.days || []).some(function (d) { return d.slots && d.slots.length; });
      if (!any && !page.week._hopped) { page.week._hopped = true; findFirstSlot(true); return; }
      drawWeek();
    }).catch(function (err) {
      page.week.loading = false;
      drawWeek();
      showError(err.message || 'We couldn\'t load availability. Please try again.');
    });
  }
  function cacheDays(data) {
    (data.days || []).forEach(function (d) { page.week.days[d.date] = d; });
  }
  function shiftWeek(dir) {
    var next = addDays(page.week.start, visibleDays() * dir);
    var first = alignStart(new Date());
    if (next.getTime() < first.getTime()) next = first;
    page.week.start = parseYMD(ymd(next));
    loadWeek();
  }
  function goToday() { page.week.start = alignStart(new Date()); loadWeek(); }

  /* Hunt forward from today for the first day with a slot, in 5-week chunks up to the horizon */
  function findFirstSlot(silent) {
    var horizon = horizonDate();
    var from = new Date();
    page.week.loading = true; drawWeek();
    var step = function () {
      if (from.getTime() > horizon.getTime()) {
        page.week.loading = false; drawWeek();
        if (!silent) showError('There are no free times in the next ' + ((page.service && page.service.horizon_days) || 90) + ' days. Please call us on 028 9266 2280.');
        return;
      }
      var to = addDays(from, FIRST_SLOT_CHUNK - 1);
      if (to.getTime() > horizon.getTime()) to = horizon;
      return page.api.availability({ service: page.service.handle, from: ymd(from), to: ymd(to), party: partySize() }).then(function (data) {
        cacheDays(data);
        markFetched(from, to);
        var first = (data.days || []).filter(function (d) { return d.slots && d.slots.length; }).sort(function (a, b) { return a.date < b.date ? -1 : 1; })[0];
        if (first) {
          page.week.loading = false;
          page.week.start = alignStart(parseYMD(first.date));
          if (!rangeFetched()) { return loadWeek().then(function () { announce('Showing from ' + fmtDayLong(page.week.start) + '.'); }); }
          drawWeek();
          announce('Showing from ' + fmtDayLong(page.week.start) + '.');
          return;
        }
        from = addDays(to, 1);
        return step();
      }).catch(function (err) { page.week.loading = false; drawWeek(); showError(err.message || 'We couldn\'t load availability.'); });
    };
    return step();
  }

  function findSlot(day, slotId) {
    var info = page.week.days[day];
    return info && info.slots ? info.slots.filter(function (s) { return s.id === slotId; })[0] : null;
  }

  function selectSlot(day, slotId) {
    var slot = findSlot(day, slotId); if (!slot) return;
    var st = page.state, prevHold = st.hold;
    st.slot = slot; st.hold = null; saveState();
    drawWeek(); renderSummary();
    announce('Selected ' + fmtDateTime(new Date(slot.start)) + '. Holding it for you.');
    if (prevHold && prevHold.hold_id) page.api.releaseHold(prevHold.hold_id).catch(function () { /* fine */ });
    page.api.hold({ slot_id: slot.id, service: page.service.handle, party: partySize() }).then(function (h) {
      st.hold = { hold_id: h.hold_id, expires_at: h.expires_at, slot_id: slot.id, party: partySize() }; saveState();
      track('slot_selected', { service: page.service.handle, start: slot.start });
      go(1);
    }).catch(function (err) {
      st.slot = null; saveState(); drawWeek(); renderSummary();
      if (err.code === 'slot_taken') { visibleKeys().forEach(function (k) { delete page.week.fetched[k]; }); loadWeek(); }
      showError(err.message || 'We couldn\'t hold that time. Please pick another.');
    });
  }

  /* ==========================================================================
     Step 2 — details (who · your details · questions · consent)
     ========================================================================== */
  function renderDetailsStep() {
    var st = page.state, d = st.details || {}, svc = page.service;
    var terms = page.root.getAttribute('data-terms-url') || '/policies/terms-of-service';
    var privacy = page.root.getAttribute('data-privacy-url') || '/policies/privacy-policy';
    var html = '<form data-form="details" novalidate>';

    /* Your details */
    if (page.signedIn) html += '<p class="bk-help" style="margin-bottom:1rem">Filled in from your account — change anything that\'s different for this visit.</p>';
    html += '<div class="bk-grid">' +
      field('first_name', 'First name', 'text', d.first_name, { autocomplete: 'given-name', required: true }) +
      field('last_name', 'Last name', 'text', d.last_name, { autocomplete: 'family-name', required: true }) +
    '</div><div class="bk-grid">' +
      field('email', 'Email', 'email', d.email, { autocomplete: 'email', required: true }) +
      field('phone', 'Mobile', 'tel', d.phone, { autocomplete: 'tel', required: true }) +
    '</div>';

    /* Who's coming */
    html += renderWho();

    /* Questions */
    var fields = questionFields(svc);
    var open = fields.filter(function (f) { return !f.sensitive; });
    var sensitive = fields.filter(function (f) { return f.sensitive; });
    if (open.length) {
      html += '<fieldset class="bk-fieldset bk-fieldset--group"><legend class="bk-legend">About your visit</legend>';
      open.forEach(function (f) { html += renderField(f, st.answers[f.key]); });
      html += '</fieldset>';
    }
    if (sensitive.length) {
      html += '<div class="bk-consent"><p class="bk-legend">Only if you want to tell us</p>';
      sensitive.forEach(function (f) {
        html += '<p class="bk-consent__purpose">' + esc(f.purpose || 'Only the colleague you meet will see this. We keep it for 12 months after your last visit, then delete it. It is never used for marketing.') + '</p>';
        html += renderField(Object.assign({}, f, { required: false }), st.answers[f.key]);
      });
      html += '<label class="bk-check"><input type="checkbox" name="consent_sensitive"' + (st.consent.sensitive ? ' checked' : '') + '><span class="bk-check__text">I\'m happy for McCalls to keep this note for my appointments<small>You can ask us to delete it at any time.</small></span></label></div>';
    }

    /* Consent */
    var c = st.consent || {};
    html += '<div class="bk-checks bk-checks--stack" style="margin-top:1rem">';
    html += '<label class="bk-check"><input type="checkbox" name="terms" required' + (c.terms ? ' checked' : '') + '><span class="bk-check__text">I\'ve read the <a href="' + attr(terms) + '" target="_blank" rel="noopener">booking terms</a> and <a href="' + attr(privacy) + '#appointments" target="_blank" rel="noopener">how McCalls uses appointment details</a>' + (svc.deposit ? '<small>Deposit: ' + esc(svc.deposit.label || '') + ', refundable on return.</small>' : '') + '</span><span class="bk-field-error" data-error-for="terms"></span></label>';
    html += '<label class="bk-check"><input type="checkbox" name="marketing"' + (c.marketing ? ' checked' : '') + '><span class="bk-check__text">Email me about McCalls events and offers<small>Optional. Unsubscribe any time.</small></span></label>';
    if (!page.signedIn) html += '<label class="bk-check"><input type="checkbox" name="create_account"' + (d.create_account ? ' checked' : '') + '><span class="bk-check__text">Create an account to save my sizes<small>Next time is quicker.</small></span></label>';
    html += '</div>';

    return html + actions({ submit: true }) + '</form>';
  }

  function renderWho() {
    var svc = page.service, st = page.state, html = '';
    var wf = whoFields(svc);
    var childrenField = wf.filter(function (f) { return f.type === 'children'; })[0];
    var schoolField = wf.filter(function (f) { return f.type === 'school'; })[0];
    var dateField = wf.filter(function (f) { return f.key === 'event_date' || f.key === 'wedding_date'; })[0];
    var companionField = wf.filter(function (f) { return f.key === 'companion'; })[0];
    if (!childrenField && !schoolField && !dateField && !companionField && !(svc.max_party > 1)) return '';

    html += '<fieldset class="bk-fieldset bk-fieldset--group"><legend class="bk-legend">Who\'s coming</legend>';
    if (schoolField) html += renderField(schoolField, st.answers[schoolField.key]);
    if (childrenField) {
      var pfChildren = (page.prefill && page.prefill.children) || [];
      html += '<div class="bk-field"><span class="bk-label">' + esc(childrenField.label || 'Children coming') + '</span>';
      if (pfChildren.length) html += '<label class="bk-check bk-check--plain"><input type="checkbox" data-action="same-children"' + (st.party.children.length && st.party.children.every(function (c) { return pfChildren.some(function (p) { return p.name === c.name; }); }) ? ' checked' : '') + '><span class="bk-check__text">Same as last time <small>' + esc(pfChildren.map(function (c) { return c.name + (c.year_group ? ' (' + c.year_group + ')' : ''); }).join(', ')) + '</small></span></label>';
      html += '<div class="bk-rows" data-rows="children">' + (st.party.children.length ? st.party.children : [{ name: '', year_group: '' }]).map(renderChildRow).join('') + '</div>';
      html += '<button type="button" class="bk-btn bk-btn--quiet" data-action="add-child">+ Add another child</button></div>';
    } else if (svc.max_party > 1) {
      var opts = '';
      for (var i = 1; i <= svc.max_party; i++) opts += '<option value="' + i + '"' + (st.party.size === i ? ' selected' : '') + '>' + i + (i === 1 ? ' (just me)' : '') + '</option>';
      html += '<div class="bk-grid"><div class="bk-field"><label class="bk-label" for="bk-party-size">How many people, including you?</label><select class="bk-select" id="bk-party-size" name="party_size">' + opts + '</select></div>';
      html += '<div class="bk-field"><label class="bk-label" for="bk-members">Names of anyone else coming<span class="bk-label__optional">(optional)</span></label><input class="bk-input" id="bk-members" name="members" value="' + attr((st.party.members || []).map(function (m) { return m.name; }).filter(Boolean).join(', ')) + '" autocomplete="off" placeholder="Separate with commas"></div></div>';
    }
    if (companionField) html += renderField(companionField, st.party.companion);
    if (dateField) html += renderField(dateField, st.event_date);
    return html + '</fieldset>';
  }

  function renderChildRow(c, i) {
    var years = ['Nursery', 'P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13', 'Year 14'];
    return '<div class="bk-row" data-row>' +
      '<div class="bk-field"><label class="bk-label" for="bk-child-name-' + i + '">First name</label><input class="bk-input" id="bk-child-name-' + i + '" name="child_name" value="' + attr(c.name || '') + '" autocomplete="off"></div>' +
      '<div class="bk-field"><label class="bk-label" for="bk-child-year-' + i + '">Year group</label><select class="bk-select" id="bk-child-year-' + i + '" name="child_year"><option value="">Choose…</option>' + years.map(function (y) { return '<option' + (c.year_group === y ? ' selected' : '') + '>' + y + '</option>'; }).join('') + '</select></div>' +
      '<button type="button" class="bk-row__remove" data-action="remove-row" aria-label="Remove this child">Remove</button>' +
    '</div>';
  }

  /* ==========================================================================
     Step 3 — confirm
     ========================================================================== */
  function renderConfirmStep() {
    var svc = page.service, st = page.state;
    var start = st.slot ? new Date(st.slot.start) : null;
    var rows = [];
    rows.push(['What', esc(svc.title) + (svc.duration_minutes ? '<small>' + esc(svc.duration_minutes) + ' minutes' + (svc.price_note ? ' · ' + esc(svc.price_note) : '') + '</small>' : ''), null]);
    rows.push(['When', start ? esc(fmtDayFull(start)) + '<small>' + esc(fmtTime(start)) + (st.slot.end ? ' – ' + esc(fmtTime(new Date(st.slot.end))) : '') + '</small>' : '—', 'time']);
    var who = whoSummary();
    if (who) rows.push(['Who', who, 'details']);
    rows.push(['Your details', esc((st.details.first_name || '') + ' ' + (st.details.last_name || '')) + '<small>' + esc(st.details.email || '') + ' · ' + esc(st.details.phone || '') + '</small>', 'details']);
    var ans = answersSummary();
    if (ans) rows.push(['Answers', ans, 'details']);
    if (svc.colleague) rows.push(['With', esc(svc.colleague.name) + '<small>' + esc(svc.colleague.role || '') + '</small>', null]);
    var loc = svc.location || page.location;
    if (loc) rows.push(['Where', esc(loc.name || 'McCalls') + '<small>' + esc(loc.address || '') + '</small>', null]);
    var html = '<dl class="bk-summary">' + rows.map(function (r) {
      return '<div><dt>' + r[0] + '</dt><dd>' + r[1] + (r[2] ? ' <a href="#" class="bk-link bk-summary__edit" data-action="goto" data-goto="' + r[2] + '">Change</a>' : '') + '</dd></div>';
    }).join('') + '</dl>';
    if (st.hold) html += renderHoldNote();
    html += '<div class="bk-actions"><button type="button" class="bk-btn bk-btn--quiet" data-action="back">' + page.icons.back + '<span>Back</span></button><button type="button" class="bk-btn" data-action="book">Confirm booking' + page.icons.tick + '</button></div>';
    return html;
  }

  function whoSummary() {
    var svc = page.service, st = page.state, bits = [];
    if (st.party.children && st.party.children.length) bits.push(st.party.children.map(function (c) { return esc(c.name) + (c.year_group ? ' (' + esc(c.year_group) + ')' : ''); }).join(', '));
    if (st.answers.school) bits.push('<small>School: ' + esc(schoolLabel(st.answers.school)) + '</small>');
    if (svc.max_party > 1 && !st.party.children.length) bits.push(st.party.size + ' ' + pluralise(st.party.size, 'person', 'people') + (st.party.members.filter(function (m) { return m.name; }).length ? '<small>' + esc(st.party.members.map(function (m) { return m.name; }).filter(Boolean).join(', ')) + '</small>' : ''));
    if (st.party.companion) bits.push('<small>Coming with: ' + esc(st.party.companion) + '</small>');
    if (st.event_date) bits.push('<small>Event: ' + esc(fmtDayFull(parseYMD(st.event_date))) + '</small>');
    return bits.join('');
  }
  function answersSummary() {
    var svc = page.service, st = page.state;
    var out = [];
    questionFields(svc).forEach(function (f) {
      var v = st.answers[f.key];
      if (v == null || v === '' || (Array.isArray(v) && !v.length)) return;
      if (f.type === 'sizes' && typeof v === 'object') v = Object.keys(v).filter(function (k) { return v[k]; }).map(function (k) { return k.replace(/_/g, ' ') + ' ' + v[k]; }).join(', ');
      if (f.type === 'toggle') v = v ? 'Yes' : 'No';
      if (f.type === 'date') v = fmtDayFull(parseYMD(v));
      out.push('<small>' + esc(f.label) + ': ' + esc(Array.isArray(v) ? v.join(', ') : v) + '</small>');
    });
    return out.join('');
  }
  function schoolLabel(handle) {
    var el = $('[data-school-name="' + handle + '"]');
    return el ? el.getAttribute('data-school-label') : (page.state.answers.school_name || handle);
  }

  /* ---------- fields ---------- */
  function field(name, label, type, value, o) {
    o = o || {};
    var id = 'bk-' + name;
    return '<div class="bk-field"><label class="bk-label" for="' + id + '">' + esc(label) + (o.required ? '' : '<span class="bk-label__optional">(optional)</span>') + '</label>' +
      '<input class="bk-input" id="' + id + '" name="' + attr(name) + '" type="' + attr(type) + '" value="' + attr(value || '') + '"' + (o.autocomplete ? ' autocomplete="' + attr(o.autocomplete) + '"' : '') + (o.required ? ' required' : '') + (type === 'tel' ? ' inputmode="tel"' : '') + (o.help ? ' aria-describedby="' + id + '-help"' : '') + '>' +
      (o.help ? '<p class="bk-help" id="' + id + '-help">' + esc(o.help) + '</p>' : '') + '<span class="bk-field-error" data-error-for="' + attr(name) + '"></span></div>';
  }

  /* Question schema field: {key,label,type,options,required,help,sensitive} */
  function renderField(f, value) {
    var id = 'bk-q-' + f.key;
    var lab = '<label class="bk-label" for="' + id + '">' + esc(f.label) + (f.required ? '' : '<span class="bk-label__optional">(optional)</span>') + '</label>';
    var help = f.help ? '<p class="bk-help" id="' + id + '-help">' + esc(f.help) + '</p>' : '';
    var desc = f.help ? ' aria-describedby="' + id + '-help"' : '';
    var err = '<span class="bk-field-error" data-error-for="' + attr(f.key) + '"></span>';
    var req = f.required ? ' required' : '';
    switch (f.type) {
      case 'textarea':
        return '<div class="bk-field">' + lab + '<textarea class="bk-textarea" id="' + id + '" name="' + attr(f.key) + '"' + req + desc + '>' + esc(value || '') + '</textarea>' + help + err + '</div>';
      case 'select':
        return '<div class="bk-field">' + lab + '<select class="bk-select" id="' + id + '" name="' + attr(f.key) + '"' + req + desc + '><option value="">Choose…</option>' + (f.options || []).map(function (o) { var v = typeof o === 'string' ? o : o.value, l = typeof o === 'string' ? o : o.label; return '<option value="' + attr(v) + '"' + (value === v ? ' selected' : '') + '>' + esc(l) + '</option>'; }).join('') + '</select>' + help + err + '</div>';
      case 'multiselect':
        var vals = Array.isArray(value) ? value : [];
        return '<fieldset class="bk-fieldset"><legend class="bk-legend bk-legend--field">' + esc(f.label) + (f.required ? '' : '<span class="bk-label__optional">(optional)</span>') + '</legend>' + help + '<div class="bk-checks">' + (f.options || []).map(function (o) { var v = typeof o === 'string' ? o : o.value, l = typeof o === 'string' ? o : o.label; return '<label class="bk-check"><input type="checkbox" name="' + attr(f.key) + '" value="' + attr(v) + '"' + (vals.indexOf(v) !== -1 ? ' checked' : '') + '><span class="bk-check__text">' + esc(l) + '</span></label>'; }).join('') + '</div>' + err + '</fieldset>';
      case 'number':
        return '<div class="bk-field">' + lab + '<input class="bk-input" id="' + id + '" name="' + attr(f.key) + '" type="number" inputmode="numeric" min="0" value="' + attr(value || '') + '"' + req + desc + '>' + help + err + '</div>';
      case 'date':
        return '<div class="bk-field">' + lab + '<input class="bk-input" id="' + id + '" name="' + attr(f.key) + '" type="date" value="' + attr(value || '') + '"' + req + desc + '>' + help + err + '</div>';
      case 'toggle':
        return '<label class="bk-check"><input type="checkbox" name="' + attr(f.key) + '"' + (value ? ' checked' : '') + '><span class="bk-check__text">' + esc(f.label) + (f.help ? '<small>' + esc(f.help) + '</small>' : '') + '</span></label>';
      case 'sizes':
        var s = value || (page.prefill && page.prefill.size_profile) || {};
        var keys = [['chest', 'Chest'], ['waist', 'Waist'], ['inside_leg', 'Inside leg'], ['collar', 'Collar'], ['shoe', 'Shoe']];
        return '<fieldset class="bk-fieldset"><legend class="bk-legend bk-legend--field">' + esc(f.label) + '<span class="bk-label__optional">(optional)</span></legend>' + help + '<div class="bk-sizes">' + keys.map(function (k) { return '<div class="bk-field"><label class="bk-label bk-small" for="' + id + '-' + k[0] + '">' + k[1] + '</label><input class="bk-input" id="' + id + '-' + k[0] + '" name="' + attr(f.key) + '.' + k[0] + '" value="' + attr(s[k[0]] || '') + '" inputmode="decimal" autocomplete="off"></div>'; }).join('') + '</div>' + (page.prefill && page.prefill.size_profile ? '<p class="bk-help">From your last visit — edit anything that\'s changed.</p>' : '') + '</fieldset>';
      case 'school':
        return '<div class="bk-field bk-school" data-school-field>' + lab + '<input class="bk-input" id="' + id + '" name="' + attr(f.key) + '_name" type="text" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="' + id + '-list" autocomplete="off" value="' + attr(page.state.answers.school_name || schoolLabel(value || '') || '') + '"' + req + desc + '><input type="hidden" name="' + attr(f.key) + '" value="' + attr(value || '') + '"><ul class="bk-school__list" id="' + id + '-list" role="listbox" hidden></ul>' + help + err + '</div>';
      case 'children':
        return ''; // rendered by renderWho
      default:
        return '<div class="bk-field">' + lab + '<input class="bk-input" id="' + id + '" name="' + attr(f.key) + '" type="text" value="' + attr(value || '') + '"' + req + desc + '>' + help + err + '</div>';
    }
  }

  /* ---------- school typeahead ---------- */
  function mountSchool() {
    var wrap = $('[data-school-field]', page.root); if (!wrap) return;
    var input = $('input[type="text"]', wrap), hidden = $('input[type="hidden"]', wrap), list = $('ul', wrap);
    var timer;
    input.addEventListener('input', function () {
      hidden.value = ''; page.state.answers.school = ''; page.state.answers.school_name = input.value;
      clearTimeout(timer);
      var qv = input.value.trim();
      if (qv.length < 2) { list.hidden = true; list.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      timer = setTimeout(function () {
        page.api.schools(qv).then(function (rows) {
          rows = Array.isArray(rows) ? rows : (rows.schools || []);
          if (!rows.length) { list.innerHTML = '<li class="bk-help">No match — keep typing, or leave it and tell us on the day.</li>'; list.hidden = false; return; }
          list.innerHTML = rows.map(function (r) { return '<li role="option"><button type="button" class="bk-school__opt" data-action="pick-school" data-handle="' + attr(r.handle || r.url || r.name) + '" data-name="' + attr(r.name) + '">' + esc(r.name) + '</button></li>'; }).join('');
          list.hidden = false; input.setAttribute('aria-expanded', 'true');
        }).catch(function () { list.hidden = true; });
      }, 180);
    });
    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) { list.hidden = true; input.setAttribute('aria-expanded', 'false'); } });
  }

  /* ---------- live region + errors ---------- */
  function announce(msg) { if (page.live) { page.live.textContent = ''; setTimeout(function () { page.live.textContent = msg; }, 30); } }
  function showError(msg) {
    var box = $('[data-flow-error]', page.root);
    if (box) { box.textContent = msg; box.scrollIntoView({ behavior: page.reduceMotion ? 'auto' : 'smooth', block: 'nearest' }); }
    announce(msg);
  }
  function fieldError(form, name, msg) {
    var input = form.querySelector('[name="' + name + '"]');
    var el = form.querySelector('[data-error-for="' + name + '"]');
    if (el) el.textContent = msg || '';
    if (input) { if (msg) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
  }
  function validateRequired(form) {
    var ok = true, first = null;
    $$('[required]', form).forEach(function (input) {
      var name = input.name; var val = (input.type === 'checkbox') ? input.checked : input.value.trim();
      var missing = !val;
      if (input.type === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { fieldError(form, name, 'Please enter an email address that looks like name@example.com.'); ok = false; first = first || input; return; }
      if (missing) { fieldError(form, name, input.type === 'checkbox' ? 'Please tick this to continue.' : 'Please fill this in.'); ok = false; first = first || input; }
      else fieldError(form, name, '');
    });
    if (!ok) { showError('A few things need filling in before we go on.'); if (first) first.focus(); }
    return ok;
  }

  /* ---------- collect the details form into state ---------- */
  function collectDetails(form) {
    var st = page.state, svc = page.service;
    ['first_name', 'last_name', 'email', 'phone'].forEach(function (k) { st.details[k] = form.querySelector('[name="' + k + '"]').value.trim(); });
    var ca = form.querySelector('[name="create_account"]'); st.details.create_account = !!(ca && ca.checked);

    // who
    if ($('[data-rows="children"]', form)) {
      st.party.children = $$('[data-row]', form).map(function (row) {
        return { name: $('[name="child_name"]', row).value.trim(), year_group: $('[name="child_year"]', row).value };
      }).filter(function (c) { return c.name; });
      if (!st.party.children.length) { showError('Please add at least one child.'); var f0 = $('[name="child_name"]', form); if (f0) f0.focus(); return false; }
      var pf = (page.prefill && page.prefill.children) || [];
      st.party.children.forEach(function (c) { var m = pf.filter(function (p) { return p.name === c.name; })[0]; if (m && m.sizes) c.last_sizes = m.sizes; });
    }
    var ps = form.querySelector('[name="party_size"]'); if (ps) st.party.size = parseInt(ps.value, 10) || 1;
    var mem = form.querySelector('[name="members"]'); if (mem) st.party.members = mem.value.split(',').map(function (s) { return s.trim(); }).filter(Boolean).map(function (n) { return { name: n }; });
    var comp = form.querySelector('[name="companion"]'); if (comp) st.party.companion = comp.value.trim();
    var ev = form.querySelector('[name="event_date"], [name="wedding_date"]'); if (ev) { st.event_date = ev.value; st.answers[ev.name] = ev.value; }
    var school = form.querySelector('[name="school"]'); if (school) { st.answers.school = school.value; st.answers.school_name = form.querySelector('[name="school_name"]').value.trim(); if (!st.answers.school && st.answers.school_name) st.answers.school = st.answers.school_name; }

    // questions
    questionFields(svc).forEach(function (f) {
      if (f.type === 'multiselect') st.answers[f.key] = $$('[name="' + f.key + '"]:checked', form).map(function (i) { return i.value; });
      else if (f.type === 'toggle') { var t = form.querySelector('[name="' + f.key + '"]'); st.answers[f.key] = !!(t && t.checked); }
      else if (f.type === 'sizes') { var o = {}; $$('[name^="' + f.key + '."]', form).forEach(function (i) { var k = i.name.split('.')[1]; if (i.value.trim()) o[k] = i.value.trim(); }); st.answers[f.key] = o; }
      else if (f.type === 'school') { var h = form.querySelector('[name="' + f.key + '"]'); if (h) st.answers[f.key] = h.value; }
      else { var el = form.querySelector('[name="' + f.key + '"]'); if (el) st.answers[f.key] = el.value.trim(); }
    });
    var cs = form.querySelector('[name="consent_sensitive"]');
    st.consent.sensitive = !!(cs && cs.checked);
    if (!st.consent.sensitive) fieldsOf(svc).filter(function (f) { return f.sensitive; }).forEach(function (f) { delete st.answers[f.key]; });

    // consent
    st.consent.terms = form.querySelector('[name="terms"]').checked;
    st.consent.marketing = !!form.querySelector('[name="marketing"]').checked;
    st.consent.accepted_at = toISOWithOffset(new Date());
    saveState();
    return true;
  }

  /* ---------- navigation ---------- */
  function go(n, opts) {
    var s = steps(); if (!s.length) return;
    page.state.step = Math.max(0, Math.min(s.length - 1, n)); saveState();
    track('step', { service: page.service && page.service.handle, step: s[page.state.step].id, index: page.state.step + 1 });
    renderFlow(Object.assign({ scroll: true }, opts || {}));
  }
  function gotoId(id) { var idx = steps().map(function (s) { return s.id; }).indexOf(id); if (idx !== -1) go(idx); }

  /* Party size may have changed on the details step: re-hold so capacity is right */
  function ensureHold() {
    var st = page.state, svc = page.service;
    if (!st.slot) return Promise.reject(Object.assign(new Error('Please choose a time first.'), { code: 'no_slot' }));
    var expired = st.hold && st.hold.expires_at && new Date(st.hold.expires_at).getTime() < Date.now();
    var same = st.hold && st.hold.slot_id === st.slot.id && st.hold.party === partySize() && !expired;
    if (same) return Promise.resolve(st.hold);
    var old = st.hold;
    return page.api.hold({ slot_id: st.slot.id, service: svc.handle, party: partySize() }).then(function (h) {
      st.hold = { hold_id: h.hold_id, expires_at: h.expires_at, slot_id: st.slot.id, party: partySize() }; saveState();
      if (old && old.hold_id && old.hold_id !== h.hold_id) page.api.releaseHold(old.hold_id).catch(function () { /* fine */ });
      return st.hold;
    });
  }

  /* ---------- book ---------- */
  function book() {
    var st = page.state, svc = page.service;
    if (!st.slot) { gotoId('time'); showError('Please choose a time first.'); return; }
    var btn = $('[data-action="book"]', page.root); if (btn) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
    announce('Confirming…');
    var party = [];
    if (st.party.children.length) party = st.party.children.map(function (c) { return { name: c.name, year_group: c.year_group, role: 'child', last_sizes: c.last_sizes || null }; });
    else if (st.party.size > 1) { party = st.party.members.map(function (m) { return { name: m.name, role: 'member' }; }); for (var i = party.length; i < st.party.size - 1; i++) party.push({ name: 'Person ' + (i + 2), role: 'member' }); }
    if (st.party.companion) party.push({ name: st.party.companion, role: 'companion' });
    var payload = {
      hold_id: null, slot_id: st.slot.id, service: svc.handle, party_size: partySize(), party: party,
      customer: { first_name: st.details.first_name, last_name: st.details.last_name, email: st.details.email, phone: st.details.phone, create_account: !!st.details.create_account, id: page.customerId || null },
      answers: st.answers, event_date: st.event_date || null,
      consent: { terms: !!st.consent.terms, sensitive: !!st.consent.sensitive, accepted_at: st.consent.accepted_at },
      marketing_opt_in: !!st.consent.marketing, source: 'storefront'
    };
    ensureHold().then(function (h) {
      payload.hold_id = h.hold_id;
      return page.api.book(payload);
    }).then(function (b) {
      track('booked', { service: svc.handle, booking_id: b.id, start: b.start });
      resetState();
      setQuery({ booked: b.id, t: b.manage_token || 'x', manage: null, party: null }, true);
      showConfirmation(b);
    }).catch(function (err) {
      if (btn) { btn.disabled = false; btn.removeAttribute('aria-disabled'); }
      if (err.code === 'slot_taken' || err.code === 'hold_expired' || err.code === 'not_found' || err.code === 'no_slot') {
        st.slot = null; st.hold = null; page.week.days = {}; page.week.fetched = {}; saveState();
        gotoId('time');
        showError('Sorry — that time was taken while you were booking. Please choose another time.');
      } else {
        showError(err.message || 'We couldn\'t complete the booking. Nothing has been taken; please try again.');
      }
    });
  }

  /* ---------- confirmation ---------- */
  function icsFor(b) {
    var start = new Date(b.start), end = new Date(b.end || (start.getTime() + 3600000));
    var loc = b.location || page.location || {};
    var lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//McCalls//Appointments//EN', 'BEGIN:VEVENT', 'UID:' + b.id + '@mccalls', 'DTSTAMP:' + icsStamp(new Date()), 'DTSTART:' + icsStamp(start), 'DTEND:' + icsStamp(end),
      'SUMMARY:' + (b.service ? b.service.title : 'Appointment') + ' — McCalls', 'LOCATION:' + ((loc.name || 'McCalls') + (loc.address ? ', ' + loc.address : '')).replace(/,/g, '\\,'), 'DESCRIPTION:' + ('Manage: ' + (b.manage_url || '')).replace(/,/g, '\\,'), 'END:VEVENT', 'END:VCALENDAR'];
    return 'data:text/calendar;charset=utf8,' + encodeURIComponent(lines.join('\r\n'));
  }
  function googleFor(b) {
    var start = new Date(b.start), end = new Date(b.end || (start.getTime() + 3600000));
    var loc = b.location || page.location || {};
    return 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent((b.service ? b.service.title : 'Appointment') + ' — McCalls') + '&dates=' + icsStamp(start) + '/' + icsStamp(end) + '&location=' + encodeURIComponent((loc.name || 'McCalls') + (loc.address ? ', ' + loc.address : '')) + '&details=' + encodeURIComponent('Manage: ' + (b.manage_url || ''));
  }
  function showConfirmation(b, opts) {
    opts = opts || {};
    var box = $('[data-booking-confirmation]'); if (!box) return;
    var svc = serviceByHandle(b.service && b.service.handle) || page.service || {};
    var start = new Date(b.start);
    var loc = b.location || page.location || svc.location || {};
    var col = b.colleague || svc.colleague;
    var cancelled = b.status === 'cancelled';
    var html = '<div class="bk-container bk-container--narrow"><div class="bk-confirm bk-confirm__card">';
    html += '<span class="bk-confirm__mark" aria-hidden="true">' + page.icons.tick + '</span>';
    html += '<span class="bk-eyebrow">' + (cancelled ? 'Cancelled' : (opts.rescheduled ? 'Rescheduled' : 'Booked')) + '</span>';
    html += '<h1 class="bk-title" tabindex="-1" data-confirm-heading>' + (cancelled ? 'Your appointment is cancelled.' : (opts.rescheduled ? 'Your new time is booked.' : 'You\'re booked in.')) + '</h1>';
    html += '<p class="bk-lede">' + (cancelled ? 'Nothing more to do. You are welcome to book again whenever you like.' : 'A confirmation with a calendar invite is on its way to ' + esc(b.customer && b.customer.email || 'you') + '.') + '</p>';
    html += '<dl class="bk-summary" style="margin-top:1.5rem">' +
      '<div><dt>What</dt><dd>' + esc(svc.title || (b.service && b.service.title) || 'Appointment') + '</dd></div>' +
      '<div><dt>When</dt><dd>' + esc(fmtDayFull(start)) + '<small>' + esc(fmtTime(start)) + (b.end ? ' – ' + esc(fmtTime(new Date(b.end))) : '') + '</small></dd></div>' +
      (col ? '<div><dt>Who</dt><dd>' + esc(col.name) + '<small>' + esc(col.role || '') + '</small></dd></div>' : '') +
      (loc.name ? '<div><dt>Where</dt><dd>' + esc(loc.name) + '<small>' + esc(loc.address || '') + '</small>' + (loc.map_url ? '<a class="bk-link bk-small" href="' + attr(loc.map_url) + '" target="_blank" rel="noopener">Map</a>' : '') + '</dd></div>' : '') +
      (b.party && b.party.length ? '<div><dt>Coming</dt><dd>' + esc(b.party.map(function (p) { return p.name; }).filter(Boolean).join(', ')) + '</dd></div>' : '') +
      '<div><dt>Reference</dt><dd>' + esc(b.id) + '</dd></div>' +
    '</dl>';
    if (!cancelled) {
      html += '<div class="bk-confirm__actions">' +
        '<a class="bk-btn bk-btn--ghost" href="' + attr(b.ics_url || icsFor(b)) + '" download="mccalls-appointment.ics">' + page.icons.cal + '<span>Add to calendar</span></a>' +
        '<a class="bk-link" href="' + attr(googleFor(b)) + '" target="_blank" rel="noopener">Google Calendar</a>' +
        (b.manage_url ? '<a class="bk-link" href="' + attr(b.manage_url) + '">Change or cancel</a>' : '') +
        (b.party_url ? '<a class="bk-link" href="' + attr(b.party_url) + '">Invite the party</a>' : '') +
      '</div>';
      html += '<div class="bk-confirm__next"><span class="bk-eyebrow">What happens next</span><ol class="bk-steps">' +
        '<li><div><h3>Confirmation email</h3><p>With a calendar invite, our address and parking.</p></div></li><li><div><h3>A reminder</h3><p>Two days before, and again on the morning.</p></div></li><li><div><h3>On the day</h3><p>Come to the front door on Bow Street; ' + esc(col ? col.name : 'we') + ' will be expecting you.</p></div></li>' +
      '</ol></div>';
    } else {
      html += '<div class="bk-confirm__actions"><a class="bk-btn" href="' + attr(page.hubUrl + '?service=' + encodeURIComponent(svc.handle || '')) + '">Book another time</a></div>';
    }
    html += '</div></div>';
    box.innerHTML = html; box.hidden = false;
    $$('[data-booking-flow-section], [data-booking-faq]').forEach(function (el) { el.hidden = true; });
    var h = $('[data-confirm-heading]', box); if (h) { h.focus(); }
    box.scrollIntoView({ behavior: page.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  /* ---------- manage view ---------- */
  function showManage(id, t) {
    var root = page.root; if (!root) return;
    root.innerHTML = '<div class="bk-flow__main"><div class="bk-skeleton" aria-hidden="true"><span></span><span></span></div><p class="bk-help">Finding your appointment…</p></div>';
    page.api.booking(id, t).then(function (b) {
      var svc = serviceByHandle(b.service.handle);
      if (svc && !page.service) showServiceView(svc.handle);
      page.service = svc || page.service;
      page.manage = { booking: b, token: t };
      renderManage(b);
    }).catch(function (err) {
      root.innerHTML = '<div class="bk-flow__main"><div class="bk-error" role="alert">' + esc(err.message || 'We couldn\'t find that appointment.') + '</div><p><a class="bk-btn" href="' + attr(page.hubUrl) + '">Book an appointment</a></p></div>';
    });
  }
  function renderManage(b, mode) {
    var root = page.root, start = new Date(b.start);
    var html = '<div class="bk-flow__main"><span class="bk-eyebrow">Your appointment</span><h2 class="bk-step__title" tabindex="-1" data-step-heading>' + esc(b.service.title) + '</h2>';
    html += '<div class="bk-error" role="alert" data-flow-error></div>';
    html += '<dl class="bk-summary"><div><dt>When</dt><dd>' + esc(fmtDayFull(start)) + '<small>' + esc(fmtTime(start)) + (b.end ? ' – ' + esc(fmtTime(new Date(b.end))) : '') + '</small></dd></div>' + (b.colleague ? '<div><dt>Who</dt><dd>' + esc(b.colleague.name) + '</dd></div>' : '') + '<div><dt>Status</dt><dd>' + esc(statusLabel(b.status)) + '</dd></div><div><dt>Reference</dt><dd>' + esc(b.id) + '</dd></div></dl>';
    if (b.status === 'cancelled') {
      html += '<div class="bk-actions"><span></span><a class="bk-btn" href="' + attr(page.hubUrl + '?service=' + encodeURIComponent(b.service.handle)) + '">Book again</a></div>';
    } else if (mode === 'reschedule') {
      page.state = page.state || defaultState(); page.state.slot = null; page.state.hold = null; page.week = { start: null, days: {}, fetched: {}, loading: false, expanded: {} };
      html += '<h3 class="bk-legend" style="margin-top:2rem">Choose a new time</h3><div class="bk-week" data-week></div>';
      html += '<div class="bk-actions"><button type="button" class="bk-btn bk-btn--quiet" data-action="manage-view">' + page.icons.back + '<span>Back</span></button><button type="button" class="bk-btn" data-action="manage-reschedule" disabled>Move to this time</button></div>';
    } else if (mode === 'cancel') {
      html += '<form data-form="cancel" novalidate><div class="bk-field"><label class="bk-label" for="bk-cancel-reason">Reason<span class="bk-label__optional">(optional)</span></label><select class="bk-select" id="bk-cancel-reason" name="reason"><option value="">Rather not say</option><option>Change of plans</option><option>Found what I needed</option><option>Booked by mistake</option><option>Something else</option></select></div>' +
        '<div class="bk-actions"><button type="button" class="bk-btn bk-btn--quiet" data-action="manage-view">' + page.icons.back + '<span>Keep it</span></button><button type="submit" class="bk-btn">Cancel appointment</button></div></form>';
    } else {
      html += '<div class="bk-actions"><a class="bk-link" href="' + attr(b.ics_url || icsFor(b)) + '" download="mccalls-appointment.ics">' + page.icons.cal + '<span>Add to calendar</span></a><span class="bk-actions__spacer"></span><button type="button" class="bk-btn bk-btn--ghost" data-action="manage-cancel">Cancel</button><button type="button" class="bk-btn" data-action="manage-reschedule-start">Change the time</button></div>';
      if (b.party_url) html += '<p class="bk-help" style="margin-top:1rem"><a class="bk-link" href="' + attr(b.party_url) + '">Invite the rest of the party</a></p>';
    }
    html += '</div>';
    root.innerHTML = html;
    if (mode === 'reschedule') mountWeek();
    var h = $('[data-step-heading]', root); if (h) h.focus();
  }
  function statusLabel(s) { return { requested: 'Requested', confirmed: 'Confirmed', rescheduled: 'Confirmed (moved)', cancelled: 'Cancelled', attended: 'Attended', no_show: 'Missed' }[s] || s; }

  /* ---------- party view ---------- */
  function showParty(id, t) {
    var root = page.root; if (!root) return;
    root.innerHTML = '<div class="bk-flow__main"><div class="bk-skeleton" aria-hidden="true"><span></span><span></span></div></div>';
    page.api.party(id, t).then(function (p) {
      var svc = serviceByHandle(p.service.handle);
      if (svc) showServiceView(svc.handle);
      var start = new Date(p.start);
      var html = '<div class="bk-flow__main"><span class="bk-eyebrow">' + esc(p.service.title) + '</span><h2 class="bk-step__title" tabindex="-1" data-step-heading>' + esc(p.host && p.host.first_name ? p.host.first_name + '\'s party' : 'The party') + '</h2>';
      html += '<div class="bk-error" role="alert" data-flow-error></div>';
      html += '<p class="bk-lede">Fitting on ' + esc(fmtDayLong(start)) + '. Add your sizes below so they\'re ready, or book your own fitting.</p>';
      html += '<ul class="bk-party__members" style="margin-top:1.5rem">' + (p.members || []).map(function (m) { return '<li><span>' + esc(m.name) + '</span><span class="bk-muted">' + (m.sizes_complete ? 'Sizes added' : 'Sizes needed') + '</span></li>'; }).join('') + '</ul>';
      html += '<form data-form="party-member" novalidate><h3 class="bk-legend">Add your sizes</h3>' + field('name', 'Your name', 'text', '', { required: true, autocomplete: 'name' }) + renderField({ key: 'sizes', label: 'Sizes', type: 'sizes' }, null) + field('email', 'Email', 'email', '', { autocomplete: 'email', help: 'So we can send you the fitting details.' }) + '<div class="bk-actions"><a class="bk-link" href="' + attr(p.book_url || page.hubUrl) + '">Book my own fitting instead</a><button type="submit" class="bk-btn">Save my sizes</button></div></form>';
      html += '<div class="bk-consent" style="margin-top:2rem"><p class="bk-legend">Invite others</p><div class="bk-copy"><input class="bk-input" readonly value="' + attr(p.invite_url) + '" aria-label="Invite link" data-invite-link><button type="button" class="bk-btn bk-btn--ghost" data-action="copy-invite">Copy link</button></div></div></div>';
      root.innerHTML = html; page.party = { id: id, token: t };
      var h = $('[data-step-heading]', root); if (h) h.focus();
    }).catch(function (err) {
      root.innerHTML = '<div class="bk-flow__main"><div class="bk-error" role="alert">' + esc(err.message || 'We couldn\'t find that party.') + '</div></div>';
    });
  }

  /* ==========================================================================
     Events
     ========================================================================== */
  function switchService(handle) {
    setQuery({ service: handle || null, booked: null, manage: null, party: null, t: null }, false);
    route();
  }
  function bindEvents() {
    // Service links anywhere on the page (booking CTAs, FAQ back links) — switch without a full reload
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-service-link]');
      if (a && !e.metaKey && !e.ctrlKey && a.getAttribute('href') && a.getAttribute('href').indexOf(page.hubUrl) === 0) {
        e.preventDefault();
        switchService(a.getAttribute('data-service-link') || '');
        window.scrollTo({ top: 0, behavior: page.reduceMotion ? 'auto' : 'smooth' });
      }
    });
    window.addEventListener('popstate', route);

    var root = page.root; if (!root) return;
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]'); if (!btn || !root.contains(btn)) return;
      var st = page.state; var act = btn.getAttribute('data-action');
      switch (act) {
        case 'back': e.preventDefault(); go(st.step - 1); break;
        case 'next': e.preventDefault(); go(st.step + 1); break;
        case 'goto': e.preventDefault(); gotoId(btn.getAttribute('data-goto')); break;
        case 'book': e.preventDefault(); book(); break;
        case 'add-child': { var rows = $('[data-rows="children"]', root); rows.insertAdjacentHTML('beforeend', renderChildRow({}, rows.children.length)); $('input', rows.lastElementChild).focus(); break; }
        case 'remove-row': { var row = btn.closest('[data-row]'); var rowsBox = row.parentNode; if (rowsBox.children.length > 1) row.remove(); else { $$('input, select', row).forEach(function (i) { i.value = ''; }); } break; }
        case 'same-children': {
          var pf = (page.prefill && page.prefill.children) || [];
          var box = $('[data-rows="children"]', root);
          if (btn.checked) {
            st.party.children = pf.map(function (c) { return { name: c.name, year_group: c.year_group || '', last_sizes: c.sizes || null }; });
            if (pf[0] && pf[0].school_handle && !st.answers.school) st.answers.school = pf[0].school_handle;
            if (box) box.innerHTML = st.party.children.map(renderChildRow).join('');
          } else {
            st.party.children = [];
            if (box) box.innerHTML = renderChildRow({}, 0);
          }
          saveState(); break;
        }
        case 'week-prev': shiftWeek(-1); break;
        case 'week-next': shiftWeek(1); break;
        case 'week-today': goToday(); break;
        case 'week-first': findFirstSlot(false); break;
        case 'week-more': { var day = btn.getAttribute('data-day'); page.week.expanded[day] = !page.week.expanded[day]; drawWeek(); var col = $('[data-action="week-more"][data-day="' + day + '"]', root); if (col) col.focus(); break; }
        case 'slot': if (page.manage && page.manage.booking) { selectManageSlot(btn.getAttribute('data-day'), btn.getAttribute('data-slot-id')); } else selectSlot(btn.getAttribute('data-day'), btn.getAttribute('data-slot-id')); break;
        case 'pick-school': {
          var wrap = btn.closest('[data-school-field]');
          $('input[type="text"]', wrap).value = btn.getAttribute('data-name');
          $('input[type="hidden"]', wrap).value = btn.getAttribute('data-handle');
          st.answers.school = btn.getAttribute('data-handle'); st.answers.school_name = btn.getAttribute('data-name'); saveState();
          $('ul', wrap).hidden = true; $('input[type="text"]', wrap).setAttribute('aria-expanded', 'false'); break;
        }
        case 'manage-view': renderManage(page.manage.booking); break;
        case 'manage-cancel': renderManage(page.manage.booking, 'cancel'); break;
        case 'manage-reschedule-start': renderManage(page.manage.booking, 'reschedule'); break;
        case 'manage-reschedule': doReschedule(); break;
        case 'copy-invite': {
          var inp = $('[data-invite-link]', root);
          if (navigator.clipboard) navigator.clipboard.writeText(inp.value).then(function () { btn.textContent = 'Copied'; announce('Link copied.'); });
          else { inp.select(); document.execCommand('copy'); btn.textContent = 'Copied'; }
          break;
        }
      }
    });
    root.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-form]'); if (!form) return;
      e.preventDefault();
      var kind = form.getAttribute('data-form');
      if (kind === 'cancel') { doCancel(form.querySelector('[name="reason"]').value); return; }
      if (kind === 'party-member') { doPartyMember(form); return; }
      if (!validateRequired(form)) return;
      if (kind === 'details') {
        if (!collectDetails(form)) return;
        var btn = form.querySelector('button[type="submit"]'); if (btn) btn.disabled = true;
        ensureHold().then(function () { go(page.state.step + 1); }).catch(function (err) {
          if (btn) btn.disabled = false;
          if (err.code === 'slot_taken' || err.code === 'no_slot') { page.state.slot = null; page.state.hold = null; page.week.days = {}; page.week.fetched = {}; saveState(); gotoId('time'); showError('Sorry — that time is no longer free for ' + partySize() + ' people. Please choose another time.'); }
          else showError(err.message || 'We couldn\'t hold that time. Please try again.');
        });
      }
    });
    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-service-select]')) {
        switchService(e.target.value);
      }
    });
    root.addEventListener('toggle', function (e) {
      if (e.target.matches && e.target.matches('[data-about]')) page.aboutOpen = e.target.open;
    }, true);
  }
  function selectManageSlot(day, slotId) {
    var slot = findSlot(day, slotId); if (!slot) return;
    page.state.slot = slot; drawWeek();
    var btn = $('[data-action="manage-reschedule"]', page.root); if (btn) btn.disabled = true;
    page.api.hold({ slot_id: slot.id, service: page.service.handle, party: 1 }).then(function (h) { page.state.hold = { hold_id: h.hold_id, slot_id: slot.id, expires_at: h.expires_at, party: 1 }; if (btn) btn.disabled = false; announce('Selected ' + fmtDateTime(new Date(slot.start)) + '.'); })
      .catch(function (err) { showError(err.message); });
  }
  function doReschedule() {
    var m = page.manage; if (!m || !page.state.hold) return;
    page.api.reschedule(m.booking.id, m.token, { hold_id: page.state.hold.hold_id, slot_id: page.state.slot.id }).then(function (b) { page.manage.booking = b; showConfirmation(b, { rescheduled: true }); })
      .catch(function (err) { if (err.code === 'slot_taken') { page.week.days = {}; page.week.fetched = {}; page.state.slot = null; renderManage(page.manage.booking, 'reschedule'); } showError(err.message || 'We couldn\'t move the appointment. Please try again.'); });
  }
  function doCancel(reason) {
    var m = page.manage; if (!m) return;
    page.api.cancel(m.booking.id, m.token, { reason: reason || null }).then(function (b) { page.manage.booking = b; showConfirmation(b); })
      .catch(function (err) { showError(err.message || 'We couldn\'t cancel the appointment. Please try again or call us.'); });
  }
  function doPartyMember(form) {
    if (!validateRequired(form)) return;
    var sizes = {}; $$('[name^="sizes."]', form).forEach(function (i) { var k = i.name.split('.')[1]; if (i.value.trim()) sizes[k] = i.value.trim(); });
    page.api.partyMember(page.party.id, page.party.token, { name: form.querySelector('[name="name"]').value.trim(), email: form.querySelector('[name="email"]').value.trim(), sizes: sizes })
      .then(function () { form.innerHTML = '<div class="bk-success" role="status">Thanks — your sizes are saved for the fitting.</div>'; showParty(page.party.id, page.party.token); })
      .catch(function (err) { showError(err.message || 'We couldn\'t save that. Please try again.'); });
  }

  /* ==========================================================================
     Routing + boot
     ========================================================================== */
  function route() {
    var handle = q('service') || page.fixedHandle || '';
    var known = !!serviceByHandle(handle);
    showServiceView(known ? handle : '');
    page.week = { start: null, days: {}, fetched: {}, loading: false, expanded: {} };
    page.manage = null; page.party = null;
    var conf = $('[data-booking-confirmation]'); if (conf) conf.hidden = true;
    $$('[data-booking-flow-section], [data-booking-faq]').forEach(function (el) { el.hidden = false; });
    if (!known) { $$('[data-booking-service-only]').forEach(function (el) { el.hidden = true; }); }

    var booked = q('booked'), manage = q('manage'), party = q('party'), t = q('t');
    if (booked) { page.api.booking(booked, t).then(function (b) { showConfirmation(b); }).catch(function () { renderShell(); loadState(); renderFlow({ focus: false }); }); return; }
    if (manage) { showManage(manage, t); return; }
    if (party) { showParty(party, t); return; }
    renderShell();
    loadState();
    if (known && page.state.step === 0 && !page.state._started) { page.state._started = true; saveState(); track('start', { service: handle }); }
    renderFlow({ focus: false });
  }

  function boot() {
    page.root = $('[data-booking-flow]');
    page.hubUrl = (page.root && page.root.getAttribute('data-hub-url')) || page.hubUrl;
    page.fixedHandle = (page.root && page.root.getAttribute('data-service')) || '';
    page.customerId = page.root && page.root.getAttribute('data-customer') || null;
    page.signedIn = !!page.customerId;
    page.prefill = parseJSON(page.root && page.root.getAttribute('data-prefill') || '{}', {}) || {};
    page.policy = (page.root && page.root.getAttribute('data-policy')) || '';
    page.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var useMock = page.root && page.root.getAttribute('data-use-mock') === 'true';
    var proxy = (page.root && page.root.getAttribute('data-proxy')) || '/apps/book';
    var mockUrl = page.root && page.root.getAttribute('data-mock');
    page.api = useMock && mockUrl ? new MockApi(mockUrl, page.hubUrl) : new ProxyApi(proxy);

    // aria-live region
    page.live = document.createElement('div'); page.live.className = 'bk-sr'; page.live.setAttribute('aria-live', 'polite'); page.live.setAttribute('role', 'status'); document.body.appendChild(page.live);

    // Services: Liquid JSON → API/fixture
    var inline = parseJSON(($('[data-booking-services]') || {}).textContent || '[]', []);
    var locJson = parseJSON(($('[data-booking-location]') || {}).textContent || 'null', null);
    page.location = locJson;
    var ready = (Array.isArray(inline) && inline.length) ? Promise.resolve(inline) : page.api.services().catch(function () { return []; });
    ready.then(function (svcs) {
      page.services = svcs || [];
      if (!page.location && page.api.location) return page.api.location().then(function (l) { page.location = l; });
    }).then(function () {
      if (!page.services.length) {
        if (page.root) page.root.innerHTML = '<div class="bk-flow__main"><div class="bk-notice">Online booking isn\'t available right now. Call us on <a href="tel:+442892662280">028 9266 2280</a> and we\'ll book you in.</div></div>';
        return;
      }
      bindEvents();
      route();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
